import { useState, useLayoutEffect } from 'react';

import {
  SIDE_MENU_POPOVER_GAP,
  SIDEMENU_WIDTH,
} from 'components/shared/side-menu/side-menu.constants';

export interface SideMenuPopoverPosition {
  top: number;
  left: number;
}

interface UseSideMenuPopoverPositionOptions {
  enabled?: boolean;
  anchorElement?: HTMLElement | null;
  fallbackAnchorElement?: HTMLElement | null;
  anchorGap?: number;
}

export interface SideMenuPopoverTarget {
  element: Element;
  isEventAnchor: boolean;
}

export const resolveSideMenuPopoverTarget = (
  selector: string,
  anchorElement: HTMLElement | null,
  fallbackAnchorElement: HTMLElement | null = null
): SideMenuPopoverTarget | null => {
  if (anchorElement?.isConnected) {
    return { element: anchorElement, isEventAnchor: true };
  }

  if (fallbackAnchorElement?.isConnected) {
    return { element: fallbackAnchorElement, isEventAnchor: false };
  }

  const element = document.querySelector(selector);

  return element ? { element, isEventAnchor: false } : null;
};

const resolvePositionLeft = (
  target: SideMenuPopoverTarget,
  rect: DOMRect,
  isCollapsed: boolean,
  anchorGap?: number
): number => {
  if (target.isEventAnchor) {
    return rect.right + (anchorGap ?? 0);
  }

  if (isCollapsed) {
    return SIDEMENU_WIDTH.collapsed + (anchorGap ?? 0);
  }

  return rect.right + (anchorGap ?? SIDE_MENU_POPOVER_GAP.default);
};

export const getSideMenuPopoverPosition = (
  target: SideMenuPopoverTarget,
  isCollapsed: boolean,
  anchorGap?: number
): SideMenuPopoverPosition => {
  const rect = target.element.getBoundingClientRect();

  return {
    top: rect.top,
    left: resolvePositionLeft(target, rect, isCollapsed, anchorGap),
  };
};

const useSideMenuPopoverPosition = (
  selector: string,
  isCollapsed: boolean,
  {
    enabled = true,
    anchorElement = null,
    fallbackAnchorElement = null,
    anchorGap,
  }: UseSideMenuPopoverPositionOptions = {}
): SideMenuPopoverPosition | null => {
  const [position, setPosition] = useState<SideMenuPopoverPosition | null>(
    null
  );

  useLayoutEffect(() => {
    if (!enabled) return undefined;

    let frameId: number | undefined;

    const updatePosition = (): void => {
      const target = resolveSideMenuPopoverTarget(
        selector,
        anchorElement,
        fallbackAnchorElement
      );

      if (!target) return;

      const nextPosition = getSideMenuPopoverPosition(
        target,
        isCollapsed,
        anchorGap
      );

      setPosition((prev) =>
        prev?.top === nextPosition.top && prev?.left === nextPosition.left
          ? prev
          : nextPosition
      );
    };

    const scheduleUpdate = (): void => {
      if (frameId !== undefined) return;

      frameId = requestAnimationFrame(() => {
        frameId = undefined;
        updatePosition();
      });
    };

    updatePosition();

    const target = resolveSideMenuPopoverTarget(
      selector,
      anchorElement,
      fallbackAnchorElement
    );
    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleUpdate)
        : null;

    if (target && resizeObserver) {
      resizeObserver.observe(target.element);
    }

    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (frameId !== undefined) cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [
    selector,
    enabled,
    anchorElement,
    fallbackAnchorElement,
    anchorGap,
    isCollapsed,
  ]);

  return enabled ? position : null;
};

export default useSideMenuPopoverPosition;
